import { makeAutoObservable, runInAction } from 'mobx';
import type {
  CommunityInviteChannel,
  CreateCommunityInvite,
  InviteAdminApi,
  InviteAdminScope,
  InviteApplicationRecord,
  InviteFollowupRecord,
  InviteIssuePolicy,
  InviteIssueStatistics,
} from '../services/inviteAdminContract.ts';

export type InviteApplicationRow = InviteApplicationRecord & { id: string };
export type InviteFollowupRow = InviteFollowupRecord & { id: string };

export class InviteAdminStore {
  private scope: InviteAdminScope | null = null;
  get appId() {
    return this.scope?.appId ?? '';
  }
  loading = false;
  busy = false;
  loaded = false;
  policy: InviteIssuePolicy | null = null;
  statistics: InviteIssueStatistics | null = null;
  applications: InviteApplicationRow[] = [];
  followups: InviteFollowupRow[] = [];
  channels: CommunityInviteChannel[] = [];
  nextApplicationId: number | undefined;
  nextChannelId: string | undefined;
  hasMoreFollowups = false;
  private api: InviteAdminApi;
  private generation = 0;

  constructor(api: InviteAdminApi) {
    this.api = api;
    makeAutoObservable<this, 'api' | 'generation'>(this, {
      api: false,
      generation: false,
    });
  }

  setScope(scope: InviteAdminScope | null) {
    this.generation++;
    this.scope = scope ? { ...scope } : null;
    this.loading = false;
    this.busy = false;
    this.loaded = false;
    this.policy = null;
    this.statistics = null;
    this.applications = [];
    this.followups = [];
    this.channels = [];
    this.nextApplicationId = undefined;
    this.nextChannelId = undefined;
    this.hasMoreFollowups = false;
  }

  private requireScope() {
    if (!this.scope?.appId || !this.scope.brandId)
      throw new Error('An application must be selected');
    return this.scope;
  }

  async load() {
    const scope = this.requireScope();
    const generation = ++this.generation;
    this.loading = true;
    this.loaded = false;
    try {
      const [policy, statistics, applications, followups, channels] =
        await Promise.all([
          this.api.policy(scope),
          this.api.statistics(scope),
          this.api.applications(scope),
          this.api.followups(scope),
          this.api.channels(scope),
        ]);
      runInAction(() => {
        if (generation !== this.generation) return;
        this.policy = policy;
        this.statistics = statistics;
        this.applications = applications.items.map((row) => ({
          ...row,
          id: String(row.inviteCodeId),
        }));
        this.nextApplicationId = applications.nextBeforeId || undefined;
        this.followups = followups.map((row) => ({ ...row, id: row.email }));
        this.hasMoreFollowups = followups.length === 100;
        this.channels = channels.items;
        this.nextChannelId =
          channels.nextBeforeId && channels.nextBeforeId !== '0'
            ? channels.nextBeforeId
            : undefined;
        this.loaded = true;
      });
    } finally {
      runInAction(() => {
        if (generation === this.generation) this.loading = false;
      });
    }
  }

  async loadMoreApplications() {
    if (this.loading || !this.nextApplicationId) return;
    const scope = this.requireScope(),
      generation = this.generation;
    this.loading = true;
    try {
      const page = await this.api.applications(scope, this.nextApplicationId);
      runInAction(() => {
        if (generation !== this.generation) return;
        this.applications.push(
          ...page.items.map((row) => ({
            ...row,
            id: String(row.inviteCodeId),
          })),
        );
        this.nextApplicationId = page.nextBeforeId || undefined;
      });
    } finally {
      runInAction(() => {
        if (generation === this.generation) this.loading = false;
      });
    }
  }

  async loadMoreFollowups() {
    if (this.loading || !this.hasMoreFollowups) return;
    const scope = this.requireScope(),
      generation = this.generation;
    this.loading = true;
    try {
      const rows = await this.api.followups(scope, this.followups.length);
      runInAction(() => {
        if (generation !== this.generation) return;
        this.followups.push(...rows.map((row) => ({ ...row, id: row.email })));
        this.hasMoreFollowups = rows.length === 100;
      });
    } finally {
      runInAction(() => {
        if (generation === this.generation) this.loading = false;
      });
    }
  }

  async loadMoreChannels() {
    if (this.loading || !this.nextChannelId) return;
    const scope = this.requireScope(),
      generation = this.generation;
    this.loading = true;
    try {
      const page = await this.api.channels(scope, this.nextChannelId);
      runInAction(() => {
        if (generation !== this.generation) return;
        this.channels.push(...page.items);
        this.nextChannelId =
          page.nextBeforeId && page.nextBeforeId !== '0'
            ? page.nextBeforeId
            : undefined;
      });
    } finally {
      runInAction(() => {
        if (generation === this.generation) this.loading = false;
      });
    }
  }

  async savePolicy(policy: InviteIssuePolicy) {
    const scope = this.requireScope(),
      generation = this.generation;
    if (this.busy || this.loading)
      throw new Error('An invitation operation is already running');
    this.busy = true;
    try {
      await this.api.savePolicy(scope, policy);
      runInAction(() => {
        if (generation === this.generation) this.policy = policy;
      });
    } finally {
      runInAction(() => {
        if (generation === this.generation) this.busy = false;
      });
    }
  }

  async createChannel(input: CreateCommunityInvite) {
    const scope = this.requireScope(),
      generation = this.generation;
    if (this.busy || this.loading)
      throw new Error('An invitation operation is already running');
    this.busy = true;
    try {
      const channel = await this.api.createChannel(scope, input);
      runInAction(() => {
        if (generation === this.generation) this.channels.unshift(channel);
      });
      return channel;
    } finally {
      runInAction(() => {
        if (generation === this.generation) this.busy = false;
      });
    }
  }

  async updateChannel(
    input: Pick<CommunityInviteChannel, 'slug' | 'maxUses' | 'enabled'>,
  ) {
    const scope = this.requireScope(),
      generation = this.generation;
    if (this.busy || this.loading)
      throw new Error('An invitation operation is already running');
    this.busy = true;
    try {
      const channel = await this.api.updateChannel(scope, input);
      runInAction(() => {
        if (generation === this.generation)
          this.channels = this.channels.map((row) =>
            row.slug === channel.slug ? channel : row,
          );
      });
    } finally {
      runInAction(() => {
        if (generation === this.generation) this.busy = false;
      });
    }
  }
}
